import { NgIf, NgFor } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ViewChild,
  EnvironmentInjector,
  Inject,
  ChangeDetectorRef,
  Input,
  Output,
  EventEmitter,
  OnInit,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatChipListbox, MatChipOption } from '@angular/material/chips';
import { MatCalendar } from '@angular/material/datepicker';
import { MatDivider } from '@angular/material/divider';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelect, MatOption } from '@angular/material/select';
import { DeliveryInformation, DeliveryMomentFlexibility, GetDeliveryMomentsResponse, GetDeliveryTimeWindow } from '@next/core-lib/cart';
import { AgrEnvironment } from '@next/core-lib/environment';
import { AgrI18nSharedModule } from '@next/core-lib/i18n';
import { AgrPipesModule } from '@next/core-lib/pipes';
import { EMPTY, Subject, catchError, debounceTime, finalize, takeUntil, tap } from 'rxjs';

import { ProductWizardMode } from 'src/app/product/models/product.models';

import { DeliveryInformationService } from './services/delivery-information.service';

import { DeliveryDateWeekendWarningComponent } from '../../../standalone/delivery-date-weekend-warning/delivery-date-weekend-warning.component';

import { FormatTimeWindowPipe } from '../../pipes/format-time-window.pipe';

@Component({
  selector: 'app-product-delivery-moment',
  templateUrl: './product-delivery-information.component.html',
  styleUrls: ['./product-delivery-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    MatIcon,
    DeliveryDateWeekendWarningComponent,
    MatCalendar,
    MatDivider,
    MatChipListbox,
    NgFor,
    MatChipOption,
    MatFormField,
    MatLabel,
    MatSelect,
    FormsModule,
    ReactiveFormsModule,
    MatOption,
    MatProgressSpinner,
    AgrI18nSharedModule,
    AgrPipesModule,
    FormatTimeWindowPipe,
  ],
})
export class ProductDeliveryInformationComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @Input() wizardMode: ProductWizardMode;
  @Input() cartId: number;
  @Input() cartLineId: string;
  @Input() skuId: number;
  @Input() allowedFlexibility = 0;
  @Input() deliveryInformation: DeliveryInformation | null;
  @Input() initialFlexibility: number | null = null;

  @Output() flexibilityChanged = new EventEmitter<number>();
  @Output() deliveryInformationIsValid = new EventEmitter<boolean>();
  @Output() deliveryInformationChanged = new EventEmitter<DeliveryInformation | null>();

  loading = true;
  initialDeliveryInformation: DeliveryInformation | null = null;
  flexibility = 0;
  previousRetrievedFlexibility: number | null = null;
  error: string | null = null;
  selectedDate: Date | null = null;
  fixedDeliveryMoments: GetDeliveryMomentsResponse | null = null;
  selectableDeliveryMoments: GetDeliveryMomentsResponse | null = null;
  selectedDeliveryInformation: DeliveryInformation | null = null;
  firstDeliveryDate: Date | null = null;
  lastDeliveryDate: Date | null = null;

  @ViewChild('calendar') calendar!: MatCalendar<Date>;
  deliveryTimeWindow: FormControl = new FormControl(null);

  private setFirstAndLastDeliveryDate(deliveryMomentsResponse: GetDeliveryMomentsResponse): void {
    if (deliveryMomentsResponse && deliveryMomentsResponse.deliveryDates.length > 0) {
      let firstDate = new Date(deliveryMomentsResponse.deliveryDates[0]);
      firstDate.setMonth(firstDate.getMonth() - 1);
      this.firstDeliveryDate = firstDate;
      this.lastDeliveryDate = new Date(deliveryMomentsResponse.deliveryDates[deliveryMomentsResponse.deliveryDates.length - 1]);
    } else {
      this.firstDeliveryDate = null;
      this.lastDeliveryDate = null;
    }
  }

  constructor(
    @Inject(EnvironmentInjector) readonly env: AgrEnvironment,
    private readonly deliveryService: DeliveryInformationService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (this.deliveryInformation) {
      this.initialDeliveryInformation = this.deliveryInformation;
    }
    this.flexibility = this.initialFlexibility ?? this.allowedFlexibility;
    this.selectedDeliveryInformation = this.deliveryInformation;

    this.loadFixedDeliveryDays();

    this.subscribeToDeliveryTimeWindowChanges();
  }

  ngAfterViewInit(): void {
    this.determineDeliveryTimeWindow(this.selectedDeliveryInformation, this.selectableDeliveryMoments);
    if (this.calendar && this.selectedDate) {
      this.initializeCalendar();
    }
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public changeDate(date: Date): void {
    if (this.calendar?.activeDate) {
      this.calendar.activeDate = date;
      this.calendar.selected = date;
      this.calendar.updateTodaysDate();
      this.selectedDate = date;
      this.handleDateSelectionChanged();
      this.cdr.markForCheck();
    }
  }

  public changeFlexibility(flexibility: number): void {
    if (this.flexibility !== flexibility) {
      this.flexibility = flexibility;
      this.flexibilityChanged.emit(flexibility);
      this.loadSelectableDeliveryDays();
    }
  }

  public dateIsADeliveryDate(date: Date): boolean {
    if (!this.selectableDeliveryMoments?.deliveryDates || !date) {
      return false;
    }

    const selectableDeliveryIsoDates = this.selectableDeliveryMoments.deliveryDates.map((dateString) => new Date(dateString).toISOString());
    const index = this.binarySearch(selectableDeliveryIsoDates, date.toISOString());
    return index >= 0;
  }

  private loadFixedDeliveryDays(): void {
    if (this.fixedDeliveryMoments !== null) return;

    this.loading = true;
    this.error = null;

    this.deliveryService
      .getDeliveryMoments(this.wizardMode, this.skuId, this.cartId, this.cartLineId)
      .pipe(
        takeUntil(this.destroy$),
        catchError((_) => {
          this.error = 'Failed to load delivery days';
          return EMPTY;
        }),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe((response) => {
        this.fixedDeliveryMoments = response;

        if (this.selectableDeliveryMoments === null) {
          this.loadSelectableDeliveryDays(true);
        }
      });
  }

  private loadSelectableDeliveryDays(isInitializing = false): void {
    this.loading = true;
    this.error = null;

    this.deliveryService
      .getDeliveryMoments(this.wizardMode, this.skuId, this.cartId, this.cartLineId, this.flexibility)
      .pipe(
        takeUntil(this.destroy$),
        catchError((_) => {
          this.error = 'Failed to load delivery days';
          return EMPTY;
        }),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe((response) => {
        this.handleSelectableDeliveryDaysResponse(response, isInitializing);
      });
  }

  private handleSelectableDeliveryDaysResponse(newSelectableDeliveryDaysResponse: GetDeliveryMomentsResponse, isInitializing: boolean) {
    this.setFirstAndLastDeliveryDate(newSelectableDeliveryDaysResponse);
    this.selectableDeliveryMoments = newSelectableDeliveryDaysResponse;
    if (isInitializing && this.selectedDate === null) {
      let initialDateIsValid = this.initialDeliveryInformation && this.dateIsADeliveryDate(new Date(this.initialDeliveryInformation?.deliveryDate));
      this.selectedDate = initialDateIsValid
        ? new Date(this.initialDeliveryInformation.deliveryDate)
        : new Date(newSelectableDeliveryDaysResponse?.proposedDate);
    }
    if (!isInitializing) {
      this.selectedDate = new Date(newSelectableDeliveryDaysResponse?.proposedDate);
    }
    this.initializeCalendar();
    this.handleDateSelectionChanged();
  }

  private handleDateSelectionChanged(): void {
    let selectableDeliveryDays = this.selectableDeliveryMoments;

    if (!this.calendar?.selected || !selectableDeliveryDays || !this.fixedDeliveryMoments) {
      return;
    }

    const isADeliveryDay = this.dateIsADeliveryDate(this.calendar.selected as Date);

    if (isADeliveryDay && (selectableDeliveryDays?.deliveryTimeWindows?.length <= 0 || (this.calendar.selected && this.deliveryTimeWindow.valid))) {
      const deliveryDates = this.fixedDeliveryMoments.deliveryDates;
      const fixedDeliveryMoments = this.fixedDeliveryMoments.deliveryDates.map((dateString) => new Date(dateString).toISOString());

      const indexOfDeliveryDate = this.binarySearch(fixedDeliveryMoments, (this.calendar.selected as Date).toISOString());
      const indexOfFirstFlexibilityDate = indexOfDeliveryDate - this.flexibility;
      const possibleDeliveryDays = deliveryDates.slice(indexOfFirstFlexibilityDate, indexOfDeliveryDate + 1);

      const deliveryInfo: DeliveryInformation = {
        deliveryDate: (this.calendar.selected as Date)?.toISOString(),
        deliveryTimeWindow: this.deliveryTimeWindow.value,
        possibleDeliveryDays: possibleDeliveryDays,
        flexibility: this.flexibility,
      };
      this.selectedDeliveryInformation = deliveryInfo;
      this.deliveryInformationChanged.emit(deliveryInfo);
      this.deliveryInformationIsValid.emit(true);
      this.cdr.markForCheck();
      return;
    }

    this.selectedDeliveryInformation = null;
    this.deliveryInformationChanged.emit(null);
    this.deliveryInformationIsValid.emit(false);
    this.cdr.markForCheck();
  }

  private binarySearch(items, value) {
    let startIndex = 0;
    let stopIndex = items.length - 1;
    let middle = Math.floor((stopIndex + startIndex) / 2);

    while (items[middle] !== value && startIndex < stopIndex) {
      //adjust search area
      if (value < items[middle]) {
        stopIndex = middle - 1;
      } else if (value > items[middle]) {
        startIndex = middle + 1;
      }

      //recalculate middle
      middle = Math.floor((stopIndex + startIndex) / 2);
    }

    //make sure it's the right value
    return items[middle] !== value ? -1 : middle;
  }

  private initializeCalendar(): void {
    if (this.calendar && this.selectedDate) {
      let selectedDate = this.selectedDate;
      this.calendar.dateFilter = (date: Date) => {
        if (!date) {
          return false;
        }
        return this.dateIsADeliveryDate(date);
      };

      this.calendar.activeDate = selectedDate;
      this.calendar.selected = selectedDate;
      this.calendar.dateClass = this.customDateClass;
      this.calendar._goToDateInView(selectedDate, 'month');
    }
  }

  private determineDeliveryTimeWindow(deliveryInformation: DeliveryInformation, deliveryMoments: GetDeliveryMomentsResponse): void {
    if (deliveryInformation?.deliveryTimeWindow) {
      this.deliveryTimeWindow.setValue(deliveryInformation.deliveryTimeWindow);
    } else {
      const defaultDeliveryTimeWindow = deliveryMoments?.deliveryTimeWindows?.find((deliveryTimeWindow) => deliveryTimeWindow.isDefault);
      this.deliveryTimeWindow.setValue(defaultDeliveryTimeWindow);
    }
    this.deliveryTimeWindow.markAsTouched();
  }

  private subscribeToDeliveryTimeWindowChanges(): void {
    this.deliveryTimeWindow.valueChanges
      .pipe(
        debounceTime(5),
        takeUntil(this.destroy$),
        tap(() => {
          this.handleDateSelectionChanged();
        }),
      )
      .subscribe();
  }

  protected compareDeliveryTimeWindows(deliveryTimeWindowA: GetDeliveryTimeWindow, deliveryTimeWindowB: GetDeliveryTimeWindow): boolean {
    return (
      (deliveryTimeWindowA?.timeFenceId === deliveryTimeWindowB?.timeFenceId ||
        (deliveryTimeWindowA?.timeFrom === deliveryTimeWindowB?.timeFrom && deliveryTimeWindowA?.timeTo === deliveryTimeWindowB?.timeTo)) ??
      false
    );
  }

  private readonly customDateClass = (date: Date, view: 'month' | 'year' | 'multi-year'): string => {
    if (view !== 'month' || this.calendar?.selected === null || this.flexibility === DeliveryMomentFlexibility.fixed || date >= this.calendar?.selected) {
      return '';
    }
    const fixedIsoDates = this.fixedDeliveryMoments?.deliveryDates?.map((dateString) => new Date(dateString).toISOString()) ?? [];
    const currentDateIndex = fixedIsoDates.indexOf(date.toISOString());
    const selectedDateIndex = fixedIsoDates.indexOf((this.calendar?.selected as Date).toISOString());

    if (currentDateIndex < 0 || selectedDateIndex <= 0) {
      return '';
    }

    const selectedDateDiff = selectedDateIndex - currentDateIndex;
    if (this.flexibility === DeliveryMomentFlexibility.noLaterThan || (selectedDateDiff <= this.flexibility && selectedDateDiff > 0)) {
      return 'product-delivery-moment__calendar-date--flexibility-selection';
    }

    return '';
  };
}
